/**
 * Node.js configuration utilities
 *
 * This module uses Node.js APIs (fs, path) and should only be used
 * in CLI commands, not in browser code.
 */

import { existsSync } from 'fs'
import { resolve, join } from 'path'
import { pathToFileURL } from 'url'

// Re-export browser-safe types
export { defineConfig, type BrixonConfig } from './config-types'

/**
 * Load configuration from brixon.config.ts
 *
 * @param cwd Working directory to search from
 * @returns Configuration object or null if not found
 */
export async function loadConfig(cwd: string = process.cwd()): Promise<import('./config-types').BrixonConfig | null> {
  const configPaths = [
    join(cwd, 'brixon.config.ts'),
    join(cwd, 'brixon.config.js'),
    join(cwd, 'brixon.config.mjs'),
  ]

  for (const configPath of configPaths) {
    if (existsSync(configPath)) {
      try {
        // Use dynamic import with file URL for cross-platform compatibility
        const configUrl = pathToFileURL(resolve(configPath)).href
        const configModule = await import(configUrl)
        return configModule.default as import('./config-types').BrixonConfig
      } catch (error) {
        // Try require for CJS configs
        try {
          // eslint-disable-next-line @typescript-eslint/no-require-imports
          const configModule = require(resolve(configPath))
          return configModule.default || configModule
        } catch {
          throw new Error(`Failed to load config from ${configPath}: ${error}`)
        }
      }
    }
  }

  return null
}
